import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_THEME,
  type ResolvedTheme,
  THEME_STORAGE_KEY,
  ThemeContext,
  type ThemePreference,
} from './theme-context'

const DARK_QUERY = '(prefers-color-scheme: dark)'

const readStoredPreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Private browsing modes throw on storage access. Fall through to the default.
  }

  return DEFAULT_THEME
}

const systemTheme = (): ResolvedTheme =>
  typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
    ? 'dark'
    : 'light'

const resolve = (preference: ThemePreference): ResolvedTheme =>
  preference === 'system' ? systemTheme() : preference

/**
 * Applies the theme to <html>. The very same work is done by the inline script
 * in index.html before first paint, so this only ever *keeps* things in sync —
 * there is no flash of the wrong theme on load.
 */
const applyTheme = (theme: ResolvedTheme): void => {
  const root = document.documentElement

  root.classList.toggle('dark', theme === 'dark')
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = theme

  // Browser chrome should match the page ground. The value is read back out of
  // the resolved custom property so it stays a token, not a literal.
  const themeColor = getComputedStyle(root).getPropertyValue('--background').trim()
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')

  if (meta && themeColor) {
    meta.content = themeColor
  }
}

type ThemeProviderProps = { children: ReactNode }

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredPreference()))

  const setPreference = useCallback((next: ThemePreference): void => {
    setPreferenceState(next)

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Storage unavailable — the choice simply will not survive a reload.
    }
  }, [])

  useEffect(() => {
    const next = resolve(preference)
    setResolved(next)
    applyTheme(next)
  }, [preference])

  // Keep following the OS while the preference is `system`.
  useEffect(() => {
    if (preference !== 'system' || typeof window.matchMedia !== 'function') return undefined

    const query = window.matchMedia(DARK_QUERY)
    const onChange = (): void => {
      const next = systemTheme()
      setResolved(next)
      applyTheme(next)
    }

    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [preference])

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
